import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Status } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * ENGINEER · TURN THE PIPES — tap pipes to turn them until the flow reaches the engine.
 *
 * A floor of pipe tiles between a tank on the left and an engine on the right. The pipes of the
 * route are bright, on light plates, and every tap turns one a quarter round; the other tiles are
 * old pipes, dark and stuck, that never turn and never carry the flow. The flow runs from the tank
 * through every route pipe joined to it, lit in the era's accent as it goes, so the guest sees at
 * once how far it gets; when it reaches the engine, the engine spins up and the round is won. An
 * arrow marks where the flow comes in by the tank, and another where it must leave for the engine,
 * which waits in a dashed ring. The status line counts the route's pipes the flow has reached:
 * PIPES 2 / 4. Turning is free: there is no mistake in this game.
 *
 * Each board is dealt by laying a route from the tank to the engine that never runs alongside
 * itself, fitting the pipe each tile of it needs (a straight or a corner), filling the other tiles
 * with stuck pipes, and then turning the route's tiles at random — so turning alone always solves
 * it. Round 1 and round 2 are a 3 × 3 floor (round 2 a little longer route, more tiles turned
 * wrong), round 3 a 4 × 4 with a longer, winding route.
 *
 * WHY THE ROUTE IS BRIGHT (Marek, 6 Oct 2026: "connect the path start to finish was missing a tile
 * to connect (or it was not obvious enough)"). No board was ever short of a pipe: 60,000 deals, all
 * solvable. But every tile looked alike and every tile turned, and the one way through was a detour
 * in a quarter of round 1's boards and nine in ten of round 3's, often doubling back beside itself.
 * A guest who tried the straight way met a straight where a corner was needed and took it for a
 * missing tile. Now the route stands out, reads as one line, and a corner is drawn as a tight elbow
 * that can never pass for a straight.
 *
 * Converted to the kit (kit.tsx): the rules as plain functions, a small view state (`PipesView`),
 * a pure `View`, the `Board` that plays it on the game clock, and the `demo` the how-to plays.
 */

/** The four sides of a tile, as bits: north, east, south, west. */
export const N = 1;
export const E = 2;
export const S = 4;
export const W = 8;
const SIDES = [N, E, S, W] as const;

export type PipeKind = 'straight' | 'bend' | 'tee';

/** Each kind's openings when it has not been turned: straight N–S, bend N–E, tee N–E–S. */
const BASE: Record<PipeKind, number> = { straight: N | S, bend: N | E, tee: N | E | S };

/** Turn a set of openings a quarter round clockwise, `turns` times. */
export function turn(mask: number, turns: number): number {
  let m = mask;
  for (let i = 0; i < ((turns % 4) + 4) % 4; i++) m = ((m << 1) | (m >> 3)) & 15;
  return m;
}

export interface Tile {
  kind: PipeKind;
  /** Quarter turns clockwise, counted up for ever (so a turn always animates forwards). */
  turns: number;
  /** An old pipe off the route: drawn dark, it never turns and never carries the flow. */
  spare?: boolean;
}

export const openings = (t: Tile) => turn(BASE[t.kind], t.turns);

export interface PipeLevel {
  rows: number;
  cols: number;
  /** The path from the tank to the engine: the fewest and the most tiles. */
  minPath: number;
  maxPath: number;
  /** The share of the spare tiles that are T-pieces. */
  tees: number;
  /** At least this many of the path's tiles start turned wrong. */
  minWrong: number;
  /** And the fewest taps that solve it are no more than this. */
  maxTaps: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const PIPE_LEVELS: readonly PipeLevel[] = easier(
  [
    { rows: 3, cols: 3, minPath: 3, maxPath: 5, tees: 0, minWrong: 2, maxTaps: 7 },
    { rows: 4, cols: 4, minPath: 6, maxPath: 9, tees: 0, minWrong: 4, maxTaps: 13 },
  ],
);

export interface PipeBoard {
  rows: number;
  cols: number;
  tiles: Tile[];
  /** The tank's row, on the left edge; the engine's row, on the right. */
  source: number;
  engine: number;
  /** The cells of the route the board was laid from, tank to engine: the only tiles that turn. */
  path: number[];
  /** For each cell on the path, the turn (0–3) that fits it into the path. */
  solution: Map<number, number>;
}

const step = (cell: number, side: number, rows: number, cols: number): number => {
  const r = Math.floor(cell / cols);
  const c = cell % cols;
  if (side === N) return r > 0 ? cell - cols : -1;
  if (side === S) return r < rows - 1 ? cell + cols : -1;
  if (side === E) return c < cols - 1 ? cell + 1 : -1;
  return c > 0 ? cell - 1 : -1;
};
const opposite = (side: number) => turn(side, 2);
const sideTowards = (from: number, to: number, cols: number) =>
  to === from - cols ? N : to === from + cols ? S : to === from + 1 ? E : W;

/**
 * A random route through the grid from (source, 0) to (engine, cols − 1), within the level's
 * length, that never runs alongside itself: no tile of it touches another of its tiles but the one
 * before and the one after, so the bright route reads as one line with no short cut to look for.
 */
function layPath(lv: PipeLevel, rand: () => number, source: number, engine: number): number[] | null {
  const { rows, cols } = lv;
  const start = source * cols;
  const goal = engine * cols + cols - 1;
  const seen = new Set<number>([start]);
  const path = [start];
  let budget = 4_000;
  const touches = (next: number, from: number) =>
    SIDES.some((side) => {
      const by = step(next, side, rows, cols);
      return by >= 0 && by !== from && seen.has(by);
    });
  const walk = (cell: number): boolean => {
    if (--budget < 0) return false;
    if (cell === goal) return path.length >= lv.minPath && path.length <= lv.maxPath;
    if (path.length >= lv.maxPath) return false;
    const sides = [...SIDES].sort(() => rand() - 0.5);
    for (const side of sides) {
      const next = step(cell, side, rows, cols);
      if (next < 0 || seen.has(next) || touches(next, cell)) continue;
      seen.add(next);
      path.push(next);
      if (walk(next)) return true;
      path.pop();
      seen.delete(next);
    }
    return false;
  };
  return walk(start) ? path : null;
}

/** The kind and turn that joins side `a` to side `b`. */
function fit(a: number, b: number): { kind: PipeKind; turns: number } {
  const want = a | b;
  for (const kind of ['straight', 'bend'] as const) {
    for (let t = 0; t < 4; t++) if (turn(BASE[kind], t) === want) return { kind, turns: t };
  }
  throw new Error('no pipe joins those sides');
}

/** Deal a board: always solvable by turning the route's tiles alone, never solved already. */
export function dealPipes(lv: PipeLevel, rand: () => number): PipeBoard {
  const { rows, cols } = lv;
  let path: number[] | null = null;
  let source = 0;
  let engine = 0;
  for (let tries = 0; tries < 60 && !path; tries++) {
    source = Math.floor(rand() * rows);
    engine = Math.floor(rand() * rows);
    path = layPath(lv, rand, source, engine);
  }
  if (!path) {
    // A plain path as a last resort: along the tank's row, then up or down the last column.
    path = [];
    for (let c = 0; c < cols; c++) path.push(source * cols + c);
    for (let r = source; r !== engine; r += engine > source ? 1 : -1) path.push((r + (engine > source ? 1 : -1)) * cols + cols - 1);
  }
  // Every tile starts as a stuck old pipe; the route's tiles are fitted over them below.
  const tiles: Tile[] = Array.from({ length: rows * cols }, () => {
    const roll = rand();
    const kind: PipeKind = roll < lv.tees ? 'tee' : roll < lv.tees + (1 - lv.tees) / 2 ? 'straight' : 'bend';
    return { kind, turns: Math.floor(rand() * 4), spare: true };
  });
  const solution = new Map<number, number>();
  path.forEach((cell, i) => {
    const inSide = i === 0 ? W : sideTowards(cell, path![i - 1]!, cols);
    const outSide = i === path!.length - 1 ? E : sideTowards(cell, path![i + 1]!, cols);
    const f = fit(inSide, outSide);
    solution.set(cell, f.turns);
    tiles[cell] = { kind: f.kind, turns: f.turns };
  });
  // Turn the path's tiles out of true: at least `minWrong` of them, then a few more at random, and
  // deal the turns again if the fewest taps that solve it come to more than the level allows.
  const board: PipeBoard = { rows, cols, tiles, source, engine, path, solution };
  const solved = path.map((cell) => tiles[cell]!);
  for (let tries = 0; tries < 30; tries++) {
    const order = [...path].sort(() => rand() - 0.5);
    path.forEach((cell, i) => (tiles[cell] = solved[i]!));
    order.forEach((cell, i) => {
      const t = tiles[cell]!;
      if (i < lv.minWrong || rand() < 0.5) {
        // A straight turned twice looks the same, so it always gets an odd turn.
        const by = t.kind === 'straight' ? 1 + 2 * Math.floor(rand() * 2) : 1 + Math.floor(rand() * 3);
        tiles[cell] = { ...t, turns: t.turns + by };
      }
    });
    if (tapsToSolve(board, tiles) <= lv.maxTaps) break;
  }
  if (flowOf(board, tiles).done) tiles[path[0]!] = { ...tiles[path[0]!]!, turns: tiles[path[0]!]!.turns + 1 };
  return board;
}

export interface Flow {
  /** Each cell the flow reaches, with the side it came in by. */
  reached: Map<number, number>;
  /** It reaches the engine. */
  done: boolean;
}

/** Where the flow gets to from the tank, through every pair of openings that meet. A stuck old pipe never carries it. */
export function flowOf(board: Pick<PipeBoard, 'rows' | 'cols' | 'source' | 'engine'>, tiles: readonly Tile[]): Flow {
  const { rows, cols } = board;
  const reached = new Map<number, number>();
  const start = board.source * cols;
  if (tiles[start]!.spare || !(openings(tiles[start]!) & W)) return { reached, done: false };
  reached.set(start, W);
  const queue = [start];
  while (queue.length) {
    const cell = queue.shift()!;
    const m = openings(tiles[cell]!);
    for (const side of SIDES) {
      if (!(m & side)) continue;
      const next = step(cell, side, rows, cols);
      if (next < 0 || reached.has(next) || tiles[next]!.spare) continue;
      if (!(openings(tiles[next]!) & opposite(side))) continue;
      reached.set(next, opposite(side));
      queue.push(next);
    }
  }
  const goal = board.engine * cols + cols - 1;
  return { reached, done: reached.has(goal) && (openings(tiles[goal]!) & E) !== 0 };
}

/** The fewest taps that would solve it the way it was laid (each tap a quarter turn clockwise). */
export function tapsToSolve(board: PipeBoard, tiles: readonly Tile[]): number {
  return board.path.reduce((sum, cell) => {
    const t = tiles[cell]!;
    const need = (((board.solution.get(cell)! - t.turns) % 4) + 4) % 4;
    return sum + (t.kind === 'straight' ? need % 2 : need);
  }, 0);
}


/** Room at each side of the floor for the tank and the engine, in board units. */
const SIDE_ROOM = 38;

/** The size of a tile on the board, in board units. */
export function tileSize(lv: Pick<PipeLevel, 'rows' | 'cols'>): number {
  return Math.floor(Math.min(80, (BOARD.w - 2 * SIDE_ROOM) / lv.cols, (BOARD.h - 24) / lv.rows));
}

/** Where the floor of tiles sits on the board: its top left corner and its tile size. */
export function floorOf(lv: Pick<PipeLevel, 'rows' | 'cols'>): { x0: number; y0: number; size: number } {
  const size = tileSize(lv);
  return { x0: (BOARD.w - size * lv.cols) / 2, y0: (BOARD.h - size * lv.rows) / 2, size };
}

/** The tile under a finger at `p`, or −1. */
export function cellAt(lv: Pick<PipeLevel, 'rows' | 'cols'>, p: Pt): number {
  const { x0, y0, size } = floorOf(lv);
  const c = Math.floor((p.x - x0) / size);
  const r = Math.floor((p.y - y0) / size);
  return c < 0 || r < 0 || c >= lv.cols || r >= lv.rows ? -1 : r * lv.cols + c;
}

// -- the view -------------------------------------------------------------------------------------

/** The kinds of pipe, by their number in a view state. */
const KINDS: readonly PipeKind[] = ['straight', 'bend', 'tee'];

/** A tile's number in a view state: its kind (0 straight, 1 bend, 2 tee), plus 3 for a stuck old pipe off the route. */
export const kindNumber = (t: Tile) => KINDS.indexOf(t.kind) + (t.spare ? 3 : 0);

/** A tile from its number in a view state and its quarter turns. */
export function tileOf(k: number, turns: number): Tile {
  const n = Math.round(k);
  return { kind: KINDS[n % 3]!, turns, spare: n >= 3 };
}

/** Everything the floor shows. */
export interface PipesView {
  /** The game clock, ms. */
  t: number;
  /** The floor: rows and columns; the tank's row (left) and the engine's (right). */
  r: number;
  c: number;
  src: number;
  eng: number;
  /** Each tile's kind (0 straight, 1 bend, 2 tee; 3 more for a stuck old pipe off the route), and its turn as drawn, in degrees (a turn glides). */
  k: readonly number[];
  a: readonly number[];
  /** The laid route's pipes the flow has reached, and how many there are. */
  n: number;
  of: number;
  /** 0 playing, 1 the engine has flow; and ms since it got it (0 till then). */
  s: 0 | 1;
  w: number;
  fx?: Fx | undefined;
}

/**
 * How tight a corner turns: the radius of its curve, as a share of the half tile. A corner is drawn
 * as a tight elbow through the middle of its tile, an L, so it can never pass for a straight's I.
 */
const ELBOW = 0.5;

/** A pipe kind's centre line, in a tile centred on 0, 0 with half-size h, before it is turned. */
function pipeLines(kind: PipeKind, h: number): string {
  const r = h * ELBOW;
  if (kind === 'straight') return `M0 ${-h} L0 ${h}`;
  if (kind === 'bend') return `M0 ${-h} L0 ${-r} A${r} ${r} 0 0 0 ${r} 0 L${h} 0`;
  return `M0 ${-h} L0 ${h} M0 0 L${h} 0`;
}

/** The flow's line through an unturned tile, drawn from the side it comes in by, so it runs the right way. */
function flowLines(kind: PipeKind, inSide: number, h: number): string {
  const end = (side: number, at = h) => (side === N ? [0, -at] : side === E ? [at, 0] : side === S ? [0, at] : [-at, 0]);
  const [x0, y0] = end(inSide);
  const outs = SIDES.filter((s) => s !== inSide && BASE[kind] & s);
  if (kind === 'bend') {
    const r = h * ELBOW;
    const [x1, y1] = end(outs[0]!);
    const [a0, b0] = end(inSide, r);
    const [a1, b1] = end(outs[0]!, r);
    // N→E is anticlockwise round the elbow; E→N the other way.
    return `M${x0} ${y0} L${a0} ${b0} A${r} ${r} 0 0 ${inSide === N ? 0 : 1} ${a1} ${b1} L${x1} ${y1}`;
  }
  return outs.map((s) => `M${x0} ${y0} L0 0 L${end(s)[0]} ${end(s)[1]}`).join(' ');
}

/** A tile's quarter turns, from the angle it is drawn at: past half way, the turn counts. */
const settled = (deg: number) => ((Math.round(deg / 90) % 4) + 4) % 4;

/** The floor, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<PipesView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const lv = { rows: view.r, cols: view.c };
  const { x0, y0, size } = floorOf(lv);
  const h = size / 2;
  const floorW = size * view.c;
  const floorH = size * view.r;
  const pipeW = size * 0.3;
  // The flow is worked out here from the tiles as drawn: it moves on as a turn passes half way.
  const tiles: Tile[] = view.k.map((k, i) => tileOf(k, settled(view.a[i]!)));
  const flow = flowOf({ rows: view.r, cols: view.c, source: view.src, engine: view.eng }, tiles);
  const done = Math.round(view.s) === 1;
  const dash = (view.t / 1000) * 34;
  const fan = (view.t * 0.04 + view.w * 0.68) % 360;
  const tankY = y0 + view.src * size + h;
  const engineY = y0 + view.eng * size + h;
  const engineX = BOARD.w - 18;
  // The pipe out of the floor to the engine, from the floor's edge to the engine's side.
  const outX = x0 + floorW - 2;
  const outW = engineX - 13 - outX;
  const lit = flow.reached.has(view.src * view.c);
  const status: Status = done
    ? { text: say(lang, '✓ THE ENGINE HAS FLOW', '✓ MOTOREN FÅR STRØM'), tone: 'ok' }
    : { text: say(lang, 'PIPES', 'RØR'), count: [Math.round(view.n), view.of], hint: say(lang, 'TAP A PIPE TO TURN IT', 'TRYKK PÅ ET RØR FOR Å SNU') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}plate`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3e4a56" />
          <stop offset="1" stopColor="#1d252c" />
        </linearGradient>
        <linearGradient id={`${id}tank`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1f262d" />
          <stop offset=".4" stopColor="#aab6c1" />
          <stop offset="1" stopColor="#1a2026" />
        </linearGradient>
        <radialGradient id={`${id}hub`} cx=".35" cy=".35" r=".7">
          <stop offset="0" stopColor={INK.hi} />
          <stop offset=".5" stopColor="#8a96a2" />
          <stop offset="1" stopColor="#2a3137" />
        </radialGradient>
      </defs>

      {/* the floor under the tiles, first, so the pipes in and out run over its rim */}
      <rect x={x0 - 4} y={y0 - 4} width={floorW + 8} height={floorH + 8} rx="8" fill="#05080b" opacity=".75" />

      {/* the start: the tank, and its pipe into the floor, the flow running in */}
      <rect x="4" y={tankY - 36} width="24" height="72" rx="10" fill={`url(#${id}tank)`} />
      <rect x="8" y={tankY - 22} width="16" height="44" rx="5" fill="#05080b" />
      <rect x="8" y={tankY - 22 + 44 * 0.25} width="16" height={44 * 0.75} rx="4" fill={accent} opacity=".75" />
      <rect x="8" y={tankY - 22 + 44 * 0.25} width="16" height="3" fill={skin.haze} opacity=".9" />
      <rect x="26" y={tankY - pipeW / 2} width={x0 - 24} height={pipeW} fill="#4a555f" />
      <rect x="26" y={tankY - pipeW / 4} width={x0 - 24} height={pipeW / 2} fill={accent} />
      <path d={`M26 ${tankY} L${x0 + 2} ${tankY}`} stroke={skin.haze} strokeWidth={pipeW * 0.24} strokeDasharray="5 9" strokeDashoffset={-dash} />

      {/* the finish: the engine, its pipe out of the floor with an arrow in it, and a ring of dashes running round it till the flow comes */}
      <rect x={outX} y={engineY - pipeW / 2} width={outW} height={pipeW} fill="#4a555f" />
      {done ? (
        <>
          <rect x={outX} y={engineY - pipeW / 4} width={outW} height={pipeW / 2} fill={accent} />
          <path d={`M${outX} ${engineY} L${outX + outW} ${engineY}`} stroke={skin.haze} strokeWidth={pipeW * 0.24} strokeDasharray="5 9" strokeDashoffset={-dash} />
        </>
      ) : (
        <path d={`M${outX + 2} ${engineY - pipeW * 0.34} L${outX + outW} ${engineY} L${outX + 2} ${engineY + pipeW * 0.34} Z`} fill={accent} />
      )}
      <g transform={`translate(${engineX} ${engineY})`}>
        <rect x="-14" y="-30" width="28" height="60" rx="9" fill={`url(#${id}tank)`} />
        <rect
          x="-16.5"
          y="-32.5"
          width="33"
          height="65"
          rx="11"
          fill="none"
          stroke={accent}
          strokeWidth="2"
          strokeDasharray={done ? undefined : '6 5'}
          strokeDashoffset={done ? undefined : -dash * 0.5}
        />
        <circle r="12" fill="#05080b" stroke={done ? accent : skin.ink} strokeOpacity={done ? 1 : 0.45} strokeWidth="2" />
        <g transform={`rotate(${fan.toFixed(1)})`}>
          {[0, 90, 180, 270].map((deg) => (
            <path key={deg} d="M0 0 C3 -4 5 -8 2 -10.5 C-1 -10.5 -3 -6 0 0 Z" fill={done ? skin.haze : '#8a96a2'} transform={`rotate(${deg})`} />
          ))}
        </g>
        <circle r="3" fill={`url(#${id}hub)`} />
      </g>

      {/* the floor's tiles */}
      {tiles.map((tile, cell) => {
        const r = Math.floor(cell / view.c);
        const c = cell % view.c;
        const cx = x0 + c * size + h;
        const cy = y0 + r * size + h;
        const turned = `translate(${cx} ${cy}) rotate(${view.a[cell]!.toFixed(1)})`;
        if (tile.spare) {
          // A stuck old pipe off the route: sunk in the floor, dark, no flanges, nothing to turn.
          return (
            <g key={cell}>
              <rect x={cx - h + 1.5} y={cy - h + 1.5} width={size - 3} height={size - 3} rx="6" fill="#0c1115" />
              <path d={pipeLines(tile.kind, h)} transform={turned} stroke="#1b2228" strokeWidth={pipeW * 0.7} fill="none" />
            </g>
          );
        }
        const inSide = flow.reached.get(cell);
        // The flow is drawn in the tile's unturned frame: turn the side it came in by back.
        const baseIn = inSide === undefined ? null : turn(inSide, -tile.turns);
        const baseMask = BASE[tile.kind];
        return (
          <g key={cell}>
            <rect x={cx - h + 1.5} y={cy - h + 1.5} width={size - 3} height={size - 3} rx="6" fill={`url(#${id}plate)`} stroke={accent} strokeOpacity=".5" strokeWidth="1.5" />
            {[
              [-1, -1],
              [1, -1],
              [-1, 1],
              [1, 1],
            ].map(([sx, sy]) => (
              <circle key={`${sx}${sy}`} cx={cx + sx! * (h - 6)} cy={cy + sy! * (h - 6)} r="1.5" fill="#a3afba" opacity=".5" />
            ))}
            <g transform={turned}>
              <path d={pipeLines(tile.kind, h)} stroke="#0b0f13" strokeWidth={pipeW + 4} fill="none" />
              <path d={pipeLines(tile.kind, h)} stroke="#6f7d8a" strokeWidth={pipeW} fill="none" />
              <path d={pipeLines(tile.kind, h)} stroke="#c6d1da" strokeWidth={pipeW * 0.28} fill="none" opacity=".6" />
              {baseIn !== null && (
                <>
                  <path d={flowLines(tile.kind, baseIn, h)} stroke={accent} strokeWidth={pipeW * 0.62} fill="none" strokeLinejoin="round" />
                  <path d={flowLines(tile.kind, baseIn, h)} stroke={skin.haze} strokeWidth={pipeW * 0.24} fill="none" strokeDasharray="5 9" strokeDashoffset={-dash} strokeLinejoin="round" />
                </>
              )}
              {/* a flange where each opening meets the tile's edge */}
              {SIDES.filter((s) => baseMask & s).map((s) => {
                const horiz = s === N || s === S;
                const ex = s === E ? h - 3 : s === W ? -h + 3 : 0;
                const ey = s === S ? h - 3 : s === N ? -h + 3 : 0;
                return (
                  <rect
                    key={s}
                    x={ex - (horiz ? pipeW * 0.72 : 3)}
                    y={ey - (horiz ? 3 : pipeW * 0.72)}
                    width={horiz ? pipeW * 1.44 : 6}
                    height={horiz ? 6 : pipeW * 1.44}
                    rx="1.5"
                    fill="#8e9aa6"
                    stroke="#0b0f13"
                    strokeWidth="1"
                  />
                );
              })}
              {tile.kind === 'tee' && <circle r={pipeW * 0.34} fill={baseIn !== null ? skin.haze : '#7d8995'} stroke="#0b0f13" strokeWidth="1" />}
            </g>
          </g>
        );
      })}
      {/* a glint on the tank's outlet while the flow is only in the tank */}
      {!lit && <circle cx={x0 - 2} cy={tankY} r={pipeW * 0.3} fill={skin.haze} opacity=".8" />}
    </BoardFrame>
  );
}

// -- the board -----------------------------------------------------------------------------------

/** A tap turns its tile over this long, on the game clock. */
const TURN_MS = 160;

/** A tile's angle as drawn: its quarter turns, the last one still gliding in for `TURN_MS`. */
const drawnAngle = (turns: number, msSinceTurn: number) => {
  const k = Math.min(1, Math.max(0, msSinceTurn / TURN_MS));
  const e = 1 - (1 - k) * (1 - k);
  return q((turns % 4) * 90 - 90 * (1 - e), 1);
};

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = PIPE_LEVELS[level]!;
  const [board] = useState(() => dealPipes(lv, seeded(Date.now() + level * 6_007)));
  const tiles = useRef<Tile[]>(board.tiles);
  const turnedAt = useRef<number[]>(board.tiles.map(() => -1e9));
  /** The furthest along the route the flow has reached: a turn that carries it further glows. */
  const best = useRef(-1);
  const wonAt = useRef<number | null>(null);

  const along = (f: Flow) => board.path.filter((c) => f.reached.has(c)).length;
  const round = useRound(props);
  // The flow at the start already reaches some way along: that much is not the guest's doing.
  if (best.current < 0) best.current = along(flowOf(board, board.tiles));

  const tap = (p: Pt) => {
    if (!round.live) return;
    const cell = cellAt(lv, p);
    // A stuck old pipe off the route does not turn.
    if (cell < 0 || tiles.current[cell]!.spare) return;
    const now = round.time();
    tiles.current = tiles.current.map((t, i) => (i === cell ? { ...t, turns: t.turns + 1 } : t));
    turnedAt.current[cell] = now;
    const f = flowOf(board, tiles.current);
    const n = along(f);
    if (f.done) {
      wonAt.current = now;
      round.win();
      return;
    }
    if (n > best.current) {
      best.current = n;
      round.hit();
    }
    round.progress(Math.min(0.95, n / board.path.length));
  };

  const now = round.now;
  const f = flowOf(board, tiles.current);
  const view: PipesView = {
    t: q(now),
    r: lv.rows,
    c: lv.cols,
    src: board.source,
    eng: board.engine,
    k: tiles.current.map(kindNumber),
    a: tiles.current.map((t, i) => drawnAngle(t.turns, now - turnedAt.current[i]!)),
    n: f.done ? board.path.length : along(f),
    of: board.path.length,
    s: round.won ? 1 : 0,
    w: wonAt.current === null ? 0 : q(now - wonAt.current),
    fx: round.fx,
  };
  return (
    <View view={round.show(view)} era={props.era} accent={props.accent} lang={lang} input={{ label: say(lang, 'Tap a pipe to turn it', 'Trykk på et rør for å snu det'), down: tap }} />
  );
}

// -- the how-to ----------------------------------------------------------------------------------

/**
 * The how-to's floor, round 1's size: the tank on the top row, the engine on the middle one, and
 * a route along the top, down, and across; the other five are stuck old pipes (kind + 3). Its
 * second pipe needs two turns and its last one.
 */
const DEMO = { rows: 3, cols: 3, source: 0, engine: 1 } as const;
const DEMO_KINDS = [0, 1, 4, 3, 1, 0, 4, 3, 4] as const;
const DEMO_TURNS = [1, 0, 1, 0, 0, 0, 3, 1, 2] as const;
/** The taps: when, and on which tile. */
const DEMO_TAPS = [
  [1_300, 1],
  [2_300, 1],
  [3_600, 5],
] as const;

/**
 * The how-to: the finger taps the second pipe twice, and the flow runs on round the bend; then the
 * last one, and the engine spins up. Each tap's turn glides in, and the count moves once it is in.
 */
function demoPipes(): DemoScript<PipesView> {
  const turns: number[] = [...DEMO_TURNS];
  const angles = () => turns.map((t) => (t % 4) * 90);
  const along = () => {
    const f = flowOf(DEMO, turns.map((t, i) => tileOf(DEMO_KINDS[i]!, t)));
    return { n: [0, 1, 4, 5].filter((c) => f.reached.has(c)).length, done: f.done };
  };
  const keys: DemoKey<PipesView>[] = [
    { at: 0, view: { t: 0, r: DEMO.rows, c: DEMO.cols, src: DEMO.source, eng: DEMO.engine, k: [...DEMO_KINDS], a: angles(), n: along().n, of: 4, s: 0, w: 0, fx: [0, 0] } },
  ];
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 300, hide: true },
    { at: 300, x: 262, y: 300, hide: false },
  ];
  const { x0, y0, size } = floorOf(DEMO);
  let wonAt = 0;
  for (const [at, cell] of DEMO_TAPS) {
    const before = along().n;
    keys.push({ at, view: { a: angles() } });
    turns[cell] = turns[cell]! + 1;
    // The angle glides past 360 rather than back round: the drawn angle counts on.
    const glide = angles();
    if (glide[cell] === 0) glide[cell] = 360;
    keys.push({ at: at + TURN_MS, view: { a: glide }, ease: 'out' });
    // Once the turn is in: the count, a glow if the flow went further, and the angle back under 360.
    const now = along();
    const further = now.n > before || now.done;
    const settle: Partial<PipesView> = { a: angles() };
    if (further) Object.assign(settle, { n: now.n, fx: [1, 0] as const }, now.done ? { s: 1 as const } : {});
    keys.push({ at: at + TURN_MS + 10, view: settle, ease: 'step' });
    // (Once the engine runs, its spin-up counts on from the moment the flow reached it.)
    if (further) keys.push({ at: at + TURN_MS + 460, view: { fx: [0, 0], ...(now.done ? { w: 450 } : {}) } });
    if (now.done) wonAt = at + TURN_MS + 10;
    const x = x0 + (cell % DEMO.cols) * size + size / 2;
    const y = y0 + Math.floor(cell / DEMO.cols) * size + size / 2;
    finger.push({ at: at - 500, x, y }, { at, x, y, down: true }, { at: at + 150, x, y, down: false });
  }
  const ms = 7_000;
  keys.push({ at: ms, view: { w: ms - wonAt } });
  keys.sort((a, b) => a.at - b.at);
  finger.push({ at: 5_000, x: 262, y: 300, hide: true });
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'TAP A BRIGHT PIPE TO TURN IT', no: 'TRYKK PÅ ET LYST RØR FOR Å SNU' },
      { at: 2_000, en: 'THE FLOW FOLLOWS THE PIPES', no: 'STRØMMEN FØLGER RØRENE' },
      { at: 3_000, en: 'JOIN THE TANK TO THE ENGINE', no: 'KOBLE TANKEN TIL MOTOREN' },
    ],
  };
}

export const game = thirtyGame<PipesView>({
  Board,
  View,
  demo: demoPipes(),
  // Nothing to get wrong here: no mistake count under the board.
  mistakes: false,
  words: {
    en: {
      instruction: 'Tap the bright pipes to turn them until the flow runs from the tank to the engine.',
      howMove: 'Tap a bright pipe to turn it a quarter round. Join the tank on the left to the engine.',
    },
    no: {
      instruction: 'Trykk på de lyse rørene for å snu dem til strømmen går fra tanken til motoren.',
      howMove: 'Trykk på et lyst rør for å snu det en kvart runde. Koble tanken til venstre til motoren.',
    },
  },
});
